import { useEffect, useRef } from 'react';
import './DragonClawSpriteAnimation.css';

export default function DragonClawSpriteAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of opponent sprite
    const frameWidth = 192;  // Width of each frame (assuming similar to other moves)
    const frameHeight = 192; // Height of the sprite
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // Initial player animation (slight scale before attack)
    playerEl.style.transition = 'transform 0.2s ease-in-out';
    playerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const anim = document.createElement('div');
    anim.className = 'dragon-claw-anim';
    anim.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    anim.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(anim);
    animRef.current = anim;
    
    // Create sparks at impact moment (frame 3)
    const sparkTimer = setTimeout(() => {
      // Flash effect
      const flash = document.createElement('div');
      flash.className = 'dragon-claw-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make opponent react
      opponentEl.classList.add('dragon-claw-hit');
      
      // Remove flash and reset opponent
      setTimeout(() => {
        flash.remove();
        opponentEl.classList.remove('dragon-claw-hit');
      }, 300);
      
      // Create dragon claw particles
      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'dragon-claw-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 25;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 500 + Math.random() * 300);
      }
    }, 300); // Sync with frame 3 impact

    // Reset player position after animation
    const resetTimer = setTimeout(() => {
      playerEl.style.transition = 'transform 0.2s ease-out';
      playerEl.style.transform = '';
    }, 500);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (anim.parentNode) {
        anim.remove();
      }
      onComplete();
    }, 800);

    // Cleanup function
    return () => {
      clearTimeout(sparkTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      
      if (opponentEl) {
        opponentEl.classList.remove('dragon-claw-hit');
      }
      
      if (anim.parentNode) {
        anim.remove();
      }
      
      // Remove any flash or particles
      const elements = container.querySelectorAll('.dragon-claw-flash, .dragon-claw-particle');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
DragonClawSpriteAnimation.moveType = 'dragon';
